import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import ProductCardGallery from "./ProductCardGallery";
import { useOrdersEnabled, COMING_SOON_MESSAGE } from "@/hooks/useOrdersEnabled";

/**
 * Editorial product card.
 * - 5:7 image with hover-peek carousel (see ProductCardGallery)
 * - Quantity-first purchase flow: the shopper picks a quantity with ± controls
 *   and then clicks Add to Order. The stepper starts at 0 on every mount and
 *   resets to 0 after a successful add — so it is never "remembered".
 * - Add to Order is disabled while qty = 0 (or below the wholesale minimum).
 */
export default function ProductCard({ product, testIdPrefix = "product-card", showOverline = true, overlineText, onNavigate }) {
  const { addItem, isWholesale, minQty } = useCart();
  const [qty, setQty] = useState(0);
  const ordersEnabled = useOrdersEnabled();

  const isPrint = product.product_type === "print";
  const detailPath = `${isPrint ? "/prints" : "/products"}/${product.slug}`;

  // Prints: grid/home always show the original square artwork (cover.jpg) —
  // the framed 5×7 preview is reserved for the print detail page.
  const gridImages = isPrint
    ? [`/prints/${product.slug}/cover.jpg`]
    : product.images;
  const galleryAspect = isPrint ? "aspect-square" : "aspect-[5/7]";
  const galleryBg = isPrint ? "bg-white" : "bg-brand-surface";

  const overline = overlineText ?? (isPrint ? "Limited Edition" : product.collection);
  const wholesalePrice = product.wholesale_price;
  const retailPrice = product.price;
  // Prints are retail-only — never surface a wholesale price on them.
  const showWholesale = !isPrint && isWholesale && wholesalePrice != null;
  // Prints ignore the wholesale minimum (retail-only).
  const effectiveMin = isPrint ? 1 : minQty;

  const canAdd = qty > 0 && (isPrint || !isWholesale || qty >= effectiveMin);

  const onDec = () => setQty(Math.max(0, qty - 1));
  const onInc = () => setQty(qty + 1);

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canAdd) {
      if (!isPrint && isWholesale && qty > 0 && qty < effectiveMin) {
        toast.error(`Wholesale minimum is ${effectiveMin} per design.`);
      }
      return;
    }
    addItem(product, qty);
    toast.success(`${qty} × ${product.title} added to your order.`);
    setQty(0);
  };

  const soldOut = !!product.sold_out;

  return (
    <div data-testid={`${testIdPrefix}-${product.slug}`} className="group flex flex-col">
      <div className="relative">
        <ProductCardGallery
          images={gridImages}
          alt={product.title}
          slug={product.slug}
          to={detailPath}
          aspectClass={galleryAspect}
          bgClass={galleryBg}
          onNavigate={onNavigate}
        />
        {soldOut && (
          <span
            data-testid={`sold-out-badge-${product.slug}`}
            className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-brand-dark text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-brand-border pointer-events-none"
          >
            Sold Out
          </span>
        )}
      </div>

      <div className="mt-6 text-center px-2">
        {showOverline && <p className="overline text-[10px]">{overline}</p>}
        <Link to={detailPath} onClick={onNavigate} className="block">
          <h3 className={`font-serif text-xl md:text-[22px] ${showOverline ? "mt-2" : ""} text-brand-dark group-hover:text-brand-accent transition-colors leading-tight`}>
            {product.title}
          </h3>
        </Link>
        {showWholesale ? (
          <div className="mt-2 text-sm leading-relaxed">
            <p className="text-brand-accent font-medium">Wholesale: ${wholesalePrice.toFixed(2)}</p>
            <p className="text-brand-neutral text-xs">MSRP: ${retailPrice?.toFixed(2)}</p>
          </div>
        ) : (
          <p className="mt-2 text-brand-neutral text-sm">${retailPrice?.toFixed(2)}</p>
        )}

        {isPrint && !soldOut && Number.isFinite(product.remaining) && Number.isFinite(product.edition_size)
          && product.remaining < product.edition_size && (
            <p
              data-testid={`remaining-${product.slug}`}
              className="mt-2 text-[10px] tracking-[0.24em] uppercase text-brand-accent"
            >
              {product.remaining} of {product.edition_size} remaining
            </p>
          )}

        <div className="mt-5 flex flex-col items-center gap-3">
          {soldOut ? (
            <p
              data-testid={`sold-out-label-${product.slug}`}
              className="text-[11px] tracking-[0.24em] uppercase text-brand-neutral"
            >
              Sold Out
            </p>
          ) : ordersEnabled !== true ? (
            <p
              data-testid={`orders-disabled-${product.slug}`}
              className="text-[11px] tracking-[0.24em] uppercase text-brand-neutral text-center"
            >
              Order request options coming soon
            </p>
          ) : (
            <>
              <div
                data-testid={`qty-stepper-${product.slug}`}
                className="inline-flex items-center border border-brand-border text-brand-dark"
              >
            <button
              type="button"
              data-testid={`qty-minus-${product.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDec(); }}
              aria-label="Decrease quantity"
              className="px-3.5 py-2 hover:text-brand-accent"
            >
              <Minus strokeWidth={1.5} className="w-3.5 h-3.5" />
            </button>
            <span
              data-testid={`qty-value-${product.slug}`}
              className="px-4 text-[13px] tabular-nums min-w-[2.4rem] text-center"
            >
              {qty}
            </span>
            <button
              type="button"
              data-testid={`qty-plus-${product.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onInc(); }}
              aria-label="Increase quantity"
              className="px-3.5 py-2 hover:text-brand-accent"
            >
              <Plus strokeWidth={1.5} className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            data-testid={`add-to-order-${product.slug}`}
            onClick={onAdd}
            disabled={!canAdd}
            className="inline-flex items-center gap-2 border border-brand-accent text-brand-accent px-6 py-2.5 text-[11px] tracking-[0.16em] uppercase font-medium hover:bg-brand-accent hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand-accent"
          >
            <ShoppingBag strokeWidth={1.5} className="w-3.5 h-3.5" /> Add to Order
          </button>
          {isWholesale && (
            <p className="text-[11px] text-brand-neutral">Wholesale minimum: {minQty} per design</p>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
